import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MoreHorizontal,
  Eye,
  Pencil,
  Trash2,
  FileText,
  Calendar,
} from "lucide-react";

import StatusBadge from "../../shared/components/StatusBadge";

const EmploymentTable = ({
  employees = [],
  data = [],
  onEmployeeClick,
  onRowClick,
  onEditEmployee,
  onDeleteEmployee,
  onViewContract,
  onEditContract,
  onRenewContract,
  loading = false,
}) => {
  const navigate = useNavigate();
  const [activeMenuId, setActiveMenuId] = useState(null);

  useEffect(() => {
    const handleOutsideClick = () => {
      setActiveMenuId(null);
    };
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const rows =
    Array.isArray(employees) && employees.length
      ? employees
      : Array.isArray(data)
        ? data
        : [];

  const handleRowClick = (employee) => {
    if (typeof onEmployeeClick === "function") {
      onEmployeeClick(employee);
      return;
    }

    if (typeof onRowClick === "function") {
      onRowClick(employee);
    }
  };

  // Loading State

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex h-72 items-center justify-center">
          <div className="text-sm text-slate-500">
            Loading employment records...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      {/* TABLE HEADER */}

      <div className="w-full overflow-hidden">
        <table className="w-full text-left border-collapse table-fixed">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/70 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <th className="py-2.5 pl-4 pr-1 w-[18%] text-left truncate">Employee</th>
              <th className="py-2.5 px-1.5 w-[12%] text-left truncate">Department</th>
              <th className="py-2.5 px-1.5 w-[14%] text-left truncate">Role</th>
              <th className="py-2.5 px-1.5 w-[13%] text-left truncate">Employment Type</th>
              <th className="py-2.5 px-1.5 w-[14%] text-left truncate">Contract Type</th>
              <th className="py-2.5 px-1.5 w-[15%] text-left truncate">Work Schedule</th>
              <th className="py-2.5 px-1.5 w-[8%] text-left truncate">Start Date</th>
              <th className="py-2.5 px-1.5 w-[7%] text-left truncate">End Date</th>
              <th className="py-2.5 pr-4 pl-1 w-[5%] text-center">Status</th>
            </tr>
          </thead>

          {/* TABLE BODY */}

          <tbody className="divide-y divide-slate-100 text-xs">
            {rows.length > 0 ? (
              rows.map((employee) => (
                <tr
                  key={employee.uuid || employee.employee_id}
                  onClick={() => handleRowClick(employee)}
                  className="group cursor-pointer transition-colors hover:bg-slate-50/80"
                >
                  {/* Employee (Name on top, Employee ID underneath) */}
                  <td className="py-2.5 pl-3 pr-1 overflow-hidden">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <div className="relative h-7 w-7 shrink-0">
                        {employee.avatar ? (
                          <img
                            src={employee.avatar}
                            alt={employee.full_name}
                            className="h-full w-full rounded-full object-cover border border-slate-200"
                          />
                        ) : (
                          <div className={`flex h-full w-full items-center justify-center rounded-full text-[10px] font-bold text-white shadow-2xs ${getInitialsBg(employee.full_name || employee.first_name)}`}>
                            {getInitials(employee.full_name || employee.first_name)}
                          </div>
                        )}
                        <span className={`absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full border border-white ${(employee.status || "").toLowerCase() === "active" ? "bg-emerald-500" : "bg-amber-400"}`} />
                      </div>
                      <div className="flex flex-col justify-center overflow-hidden">
                        <span className="font-semibold text-slate-800 group-hover:text-[#147db5] transition-colors truncate leading-tight text-xs">
                          {employee.full_name ||
                            `${employee.first_name || ""} ${employee.last_name || ""}`.trim() ||
                            "Unnamed Employee"}
                        </span>
                        <span className="text-[10px] font-medium text-slate-400 leading-tight mt-0.5 truncate">
                          {employee.employee_id || "—"}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Department */}
                  <td className="py-2.5 px-1.5 text-slate-600 font-medium truncate">
                    {employee.department_name || "Engineering"}
                  </td>

                  {/* Role / Designation */}
                  <td className="py-2.5 px-1.5 text-slate-600 font-medium truncate">
                    {employee.designation || "Software Developer"}
                  </td>

                  {/* Employment Type */}
                  <td className="py-2.5 px-1.5 truncate">
                    {getEmploymentTypeBadge(employee.employment_type_name || "Full-Time Permanent")}
                  </td>

                  {/* Contract Type */}
                  <td className="py-2.5 px-1.5 text-slate-700 font-semibold truncate">
                    {employee.contract_type || "Permanent Agreement"}
                  </td>

                  {/* Work Schedule */}
                  <td className="py-2.5 px-1.5 text-slate-600 font-medium truncate">
                    {employee.work_schedule_name || "General Shift (09:00 - 18:00)"}
                  </td>

                  {/* Start Date */}
                  <td className="py-2.5 px-1.5 text-slate-600 truncate">
                    {formatDate(employee.start_date || employee.joining_date || "2026-01-01")}
                  </td>

                  {/* End Date */}
                  <td className="py-2.5 px-1.5 text-slate-600 truncate">
                    {employee.end_date ? (
                      formatDate(employee.end_date)
                    ) : (
                      <span className="text-slate-400 font-normal italic text-[11px]">
                        Permanent
                      </span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="py-2.5 px-1 text-center">
                    <div className="flex items-center justify-center">
                      <span
                        title={employee.status || "Active"}
                        className={`h-2.5 w-2.5 rounded-full ring-4 transition-transform hover:scale-125 ${
                          (employee.status || "").toLowerCase() === "active"
                            ? "bg-emerald-500 ring-emerald-500/20"
                            : (employee.status || "").toLowerCase() === "draft"
                            ? "bg-amber-500 ring-amber-500/20"
                            : "bg-slate-400 ring-slate-400/20"
                        }`}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={9}
                  className="px-6 py-16 text-center"
                >
                  <div className="mx-auto max-w-sm">
                    <p className="text-sm font-medium text-slate-700">
                      No employment records found
                    </p>

                    <p className="mt-1 text-xs text-slate-400">
                      Try changing your filters or
                      search criteria.
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};



const getInitials = (name) => {
  if (!name) return "EMP";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const getInitialsBg = (name) => {
  const colors = [
    "bg-blue-600",
    "bg-indigo-600",
    "bg-[#147db5]",
    "bg-emerald-600",
    "bg-purple-600",
    "bg-violet-600",
    "bg-teal-600",
    "bg-sky-600",
  ];
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) {
    hash = (name || "").charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getEmploymentTypeBadge = (typeName) => {
  if (!typeName || typeName === "—") {
    return (
      <span className="text-slate-400 font-normal italic text-[11px]">
        Not assigned yet
      </span>
    );
  }
  const name = typeName.toLowerCase();
  let classes = "bg-slate-50 text-slate-600 border-slate-100";
  if (name.includes("full-time") || name.includes("fulltime")) {
    classes = "bg-blue-50 text-blue-700 border-blue-100";
  } else if (name.includes("contract") || name.includes("contractor")) {
    classes = "bg-emerald-50 text-emerald-700 border-emerald-100";
  } else if (name.includes("part-time") || name.includes("parttime")) {
    classes = "bg-purple-50 text-purple-700 border-purple-100";
  } else if (name.includes("intern")) {
    classes = "bg-amber-50 text-amber-700 border-amber-100";
  }
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${classes}`}>
      {typeName}
    </span>
  );
};

const formatDate = (date) => {
  if (!date) {
    return "—";
  }

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return parsedDate.toLocaleDateString(
    "en-GB",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  );
};

export default EmploymentTable;